import { Badge, Box, Flex, Icon, Spinner, Stack, Tabs, Text } from '@chakra-ui/react';
import { forwardRef, ReactNode, useImperativeHandle, useRef } from 'react';
import { Link, matchPath, useLocation, useNavigation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { HEADER_BAR_HEIGHT, HEADER_TAB_HEIGHT, HEADER_TITLE_HEIGHT, HEADER_Z_INDEX } from './layout-constants';

export interface TabProps {
    label: string;
    link: string;
    icon?: ReactNode;
    badge?: number;
}

interface ContentProps {
    children: {
        actions?: React.ReactNode;
        bar?: React.ReactNode;
        content: React.ReactNode;
        header?: React.ReactNode;
        topBar?: React.ReactNode;
    };
    headerTitle?: string;
    headerBadge?: string;
    headerIcon?: ReactNode;
    backNavLink?: string;
    tabs?: Array<TabProps>;
    hideHeader?: boolean;
    padding?: boolean;
    hideLoader?: boolean;
}

function isTabMatch(tabLink: string, pathname: string): boolean {
    return !!(matchPath({ path: tabLink, end: true }, pathname) || matchPath({ path: `${tabLink}/*`, end: false }, pathname));
}

const MusaTabs = ({ tabs, backNavLink }: Partial<ContentProps>) => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentTab = tabs
        ?.slice()
        .sort((a, b) => b.link.length - a.link.length)
        .find((tab) => isTabMatch(tab.link, location.pathname));

    return (
        <Tabs.Root
            value={currentTab?.label}
            onValueChange={({ value }) => {
                const tab = tabs?.find((t) => t.label === value);
                if (tab) {
                    navigate(tab.link);
                }
            }}
            bg={{ base: { base: 'musa.offWhite', _dark: 'musa.dark.100' }, md: { base: 'musa.offWhite', _dark: 'musa.dark.200' } }}>
            <Tabs.List overflowX="auto" pl={backNavLink ? '12' : '5'}>
                {tabs?.map((tab) => {
                    return (
                        <Tabs.Trigger
                            flexShrink={0}
                            value={tab.label}
                            key={tab.label}
                            textTransform="capitalize"
                            whiteSpace="nowrap"
                            _hover={{ backdropFilter: 'brightness(95%)' }}
                            _focusVisible={{
                                outline: 'none',
                                boxShadow: 'inset 0 0 0 2px var(--chakra-colors-purple-500)',
                                borderRadius: 'sm',
                            }}>
                            <Stack direction="row" gap="2" alignItems="center" h="100%">
                                {tab.icon && <Icon>{tab.icon}</Icon>}
                                {tab.label}
                                {tab.badge !== undefined && tab.badge > 0 && (
                                    <Badge size="sm" colorPalette="red" variant="solid" borderRadius="full">
                                        {tab.badge}
                                    </Badge>
                                )}
                            </Stack>
                        </Tabs.Trigger>
                    );
                })}
            </Tabs.List>
        </Tabs.Root>
    );
};

export default forwardRef<{ scrollTop: () => void }, ContentProps>(function Content(
    { children, headerTitle, headerBadge, headerIcon, backNavLink, tabs = [], hideHeader, padding = true, hideLoader = false },
    ref,
) {
    const innerRef = useRef<HTMLDivElement>(null);
    const navigation = useNavigation();
    const isNavigating = Boolean(navigation.location);

    useImperativeHandle(ref, () => {
        return {
            scrollTop() {
                innerRef.current?.scrollTo(0, 0);
            },
        };
    }, []);
    const barSize = children?.bar ? HEADER_BAR_HEIGHT : 0;
    const header = children.header;
    const tabSize = tabs.length ? HEADER_TAB_HEIGHT : 0;
    const headerSize = HEADER_TITLE_HEIGHT + barSize + tabSize;
    const showLoading = isNavigating && !hideLoader;

    return (
        <Flex
            flexDirection="column"
            borderRadius={{ base: 0, md: '8px' }}
            bg={{ base: 'musa.offWhite', _dark: 'musa.dark.200' }}
            flexGrow="1"
            height="100%"
            position="relative"
            overflow="hidden">
            {!hideHeader && (
                <Box position="absolute" w="100%" zIndex={HEADER_Z_INDEX}>
                    <Flex
                        alignItems="center"
                        h={`${HEADER_TITLE_HEIGHT}px`}
                        pr="8px"
                        pl="40px"
                        borderBottom="1px solid"
                        borderBottomColor={{ base: 'musa.light.border', _dark: 'musa.dark.border' }}
                        justifyContent="space-between"
                        bg={{
                            base: { base: 'musa.offWhite', _dark: 'musa.dark.100' },
                            md: { base: 'musa.offWhite', _dark: 'musa.dark.200' },
                        }}>
                        <Flex alignItems="center" gap="8px" h="32px">
                            {backNavLink && (
                                <Box lineHeight="1">
                                    <Link to={backNavLink}>
                                        <X width="20px" height="20px" />
                                    </Link>
                                </Box>
                            )}
                            {header ? (
                                header
                            ) : (
                                <>
                                    {headerIcon && (
                                        <Flex
                                            alignItems="center"
                                            justifyContent="center"
                                            width="28px"
                                            height="28px"
                                            color="black"
                                            borderRadius="100">
                                            {headerIcon}
                                        </Flex>
                                    )}

                                    <Flex gap="2">
                                        <Text
                                            color={{ base: 'gray.800', _dark: 'white' }}
                                            lineHeight="1"
                                            textTransform="capitalize"
                                            fontWeight={500}
                                            fontSize="18px"
                                            data-testid="content-title">
                                            {headerTitle}
                                        </Text>
                                        {headerBadge && (
                                            <Badge size="sm" variant="subtle" fontSize="9px" fontWeight="bold">
                                                {headerBadge.toUpperCase()}
                                            </Badge>
                                        )}
                                    </Flex>
                                </>
                            )}
                        </Flex>
                        {children.actions}
                    </Flex>
                    {tabs.length ? <MusaTabs tabs={tabs} backNavLink={backNavLink} /> : <Flex />}
                    {children?.bar && (
                        <Box position="relative" mb="30px">
                            <Flex position="absolute" flexDirection="column" w="100%">
                                {children?.bar}
                            </Flex>
                        </Box>
                    )}
                </Box>
            )}
            {!hideHeader && <Box flexBasis={`${headerSize}px`} flexShrink={0} />}
            {children.topBar}
            <Box
                ref={innerRef}
                overflowY="auto"
                className="content"
                flexBasis="auto"
                flexGrow={1}
                px={{ base: padding ? '16px' : 0, md: padding ? '48px' : 0 }}
                pt={{ base: padding ? '24px' : 0, md: padding ? '32px' : 0 }}
                pb={padding ? '16px' : 0}>
                {showLoading ? (
                    <Flex justifyContent="center" alignItems="center" h="100%" w="100%">
                        <Spinner borderWidth="4px" animationDuration="0.65s" color="purple.500" size="lg" />
                    </Flex>
                ) : (
                    children.content
                )}
            </Box>
        </Flex>
    );
});
